import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisLanguageService,
	AlarisTableStateService,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR
} from '@campaign-portal/components-library';
import { MDTask } from '@helpers/types/models';
import { ManualDippingService } from './manual-dipping.service';
import { ManualDippingFieldsService } from './manual-dipping.fields.service';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from 'src/app/auth/user.service';
import { CreateManualDippingComponent } from './create-manual-dipping/create-manual-dipping.component';
import { SourcesService } from '../sources/sources.service';
import { ManualDippingDeleteDialogComponent } from './dialog/dialog.component';
import { ManualDippingResultsComponent } from './manual-dipping-results/manual-dipping-results.component';
import { FilterType } from '@campaign-portal/namespace/common/rpc.params';
import { UsersService } from '../users/users.service';
import { AuthGuardService } from 'src/app/auth/auth.guard';

enum ManualDippingFilterVariables {
	MDTASKID = 'md_task_id',
	STATUS = 'status',
	NUMBERS = 'numbers',
	AUTHOR = 'author'
}

@Component({
	selector: 'app-manual-dipping',
	standalone: false,
	templateUrl: './manual-dipping.component.html',
	styleUrls: [
		'./manual-dipping.component.scss',
		'../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	]
})
export class ManualDippingComponent extends AlarisComplexTableComponent<MDTask> implements OnInit {
	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-info',
			label: 'actions.view',
			action: this.viewResults.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['mdtask.read']);
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['mdtask.edit']);
			}
		}
	];

	readonly exporting$ = new BehaviorSubject<boolean>(false);

	constructor(
		public override readonly fieldsService: ManualDippingFieldsService,
		tableSettingsService: TableSettingsService,
		readonly manualDippingService: ManualDippingService,
		protected readonly user: UserService,
		private readonly dialog: AlarisDialogService,
		private readonly sources: SourcesService,
		private readonly users: UsersService,
		private readonly langService: AlarisLanguageService,
		private readonly authGuardService: AuthGuardService
	) {
		super(manualDippingService, fieldsService, tableSettingsService, TableTypes.INTERNAL);
		this.state
			.filters()
			.set(ManualDippingFilterVariables.MDTASKID, { enabled: true, filterType: FilterType.LIKE })
			.set(ManualDippingFilterVariables.STATUS, { enabled: true, filterType: FilterType.LIKE })
			.set(ManualDippingFilterVariables.NUMBERS, { enabled: true, filterType: FilterType.LIKE })
			.set(ManualDippingFilterVariables.AUTHOR, { enabled: true, filterType: FilterType.LIKE });
	}

	override ngOnInit(): void {
		super.ngOnInit();
		this.setRefBooksInFields({
			status: {
				list$: new BehaviorSubject<{ name: string; value: string }[]>([
					{ name: this.langService.translate('manual_dipping.statuses.completed'), value: 'completed' },
					{ name: this.langService.translate('manual_dipping.statuses.in_progress'), value: 'in_progress' },
					{ name: this.langService.translate('manual_dipping.statuses.failed'), value: 'failed' }
				])
			}
		});
		this.sources.load().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
		this.users
			.load()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.setRefBooksInFields({
					author: {
						list$: new BehaviorSubject<{ name: string; value: number }[]>(
							this.users.list.map((user) => {
								return {
									name: user.Login,
									value: user.ID
								};
							})
						)
					}
				});
			});
		this.manualDippingService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
			if (val) {
				this.refresh();
			}
		});
		if (
			this.manualDippingService.lastMdTask &&
			/^\/manual_dipping_results\/\d+$/.test(this.authGuardService.previousUrl.getValue())
		) {
			this.viewResults(this.manualDippingService.lastMdTask);
		}
	}

	export(): void {
		this.exporting$.next(true);
		this.manualDippingService
			.export(this.total, this.state.readParams)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.exporting$.next(false);
			});
	}

	createNewTask(): void {
		this.dialog
			.open<boolean>(CreateManualDippingComponent, { autoFocus: false })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.load();
				}
			});
	}

	delete(mdTask: MDTask): void {
		this.dialog
			.open<boolean>(ManualDippingDeleteDialogComponent, { autoFocus: false, data: { mdTask } })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.load();
				}
			});
	}

	viewResults(mdTask: MDTask): void {
		this.manualDippingService.lastMdTask = mdTask;
		this.dialog.open<boolean>(ManualDippingResultsComponent, { autoFocus: false, data: { mdTask } });
	}
}
